import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import SearchIcon from '@atlaskit/icon/glyph/search';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import Form, { Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { analytics, AnalyticsTrackSubject, AnalyticsTrackAction } from '@atlassian/temp-analytics';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import { createContactRelationship, updateEntitlementPlan, patchContactRelationship } from '@atlassian/temp-proxies';
import { TransitionTime, UpdateOrderRequest } from '@atlassian/cssxp-typescommon';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import {
  BillingFrequency,
  BillingStatus,
  ContactInfo,
  EndCustomerContact,
  EntitlementDetails,
  FetchLinkedEntitlementsPpcQuery,
  FetchLinkedEntitlementsPpcQueryVariables,
  FetchTransactionAccountForSetInvoiceGroupPaneQuery,
  FetchTransactionAccountForSetInvoiceGroupPaneQueryVariables,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  LinkedProductTypes,
  PricingType,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  BillingProfileFormField,
  BillingProfileSelectItem,
  Divider,
  ErrorComponents,
  InfoPanel,
  InvoiceGroupSelectItem,
  NotificationTypes,
  errorNotificationBuilder,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  useFeatureToggle,
  withApollo,
  CustomPanel,
  ContactsTooltip,
  ContactsTooltipType,
} from '@css-xp/base';
import {
  currentEntitlementType,
  getLinkedEntitlementsDetails,
  getParentContainerId,
} from '@css-xp/base/entitlement-utils';
import WarningDialog from '@css-xp/entitlements-v2/common/sidebar-panel/warning-dialog';
import { useConsentHub } from '@css-xp/ppc-consent-hub';
import { HelpContent, getSupportUrl } from '@css-xp/ppc-common';
import { routePaths, STATSIG_FEATURE_GATES, ELIGIBLE_BILLING_PROFILES_FOR_AMENDMENT } from '@css-xp/ppc-constants';
import { getPartnerServiceDeskUrlForGenericHelp, trackPartnerServiceDeskClick } from '@css-xp/ppc-navigation';
import {
  getDestinationLinkedEntitlementsType,
  getMaxLimitOfCustomerContact,
  getRouteUrl,
  isCollectionProductEntitlement,
  isProductBelongsToJiraFamily,
  sortBillingProfiles,
} from '@css-xp/ppc-utils';
import Textfield from '@atlaskit/textfield';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { Box, Flex, Text } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import { LinkedEntitlementsTable } from '../../components/linked-entitlements-table';
import { EditSubscriptionTermsAndConditions } from '../../components/terms-and-conditions/terms-and-conditions-details';
import { fetchTransactionAccountForSetInvoiceGroupPane } from '../../../queries/fetch-transaction-account-for-set-invoice-group-pane';
import './entitlement-set-invoice-group-pane.scss';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { AddContactSelectFields, AddContactFields } from '@css-xp/ppc-add-contact-fields';
import { useContactServicePayload, useFetchEccFromContactService } from '@css-xp/contact-resources';
import {
  CreateContactRelationshipsRequest,
  PatchContactRelationshipsRequest,
} from '@css-xp/contact-resources/src/constant';
import { fetchLinkedEntitlementsPPC } from '../../../queries/fetch-linked-entitlements-ppc';

interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isSandboxDecouplingEnabled?: boolean;
}

interface FormData {
  entitlementId?: string;
  invoiceGroupId?: InvoiceGroupSelectItem;
}

export const DEFAULT_NOTIFICATION_VISIBLE_TIME = 5000;
const getDefaultBillingGroup = (
  invoiceGroups: NonNullable<
    FetchTransactionAccountForSetInvoiceGroupPaneQuery['transactionAccount']['availableInvoiceGroups']
  >['nodes'],
  invoiceGroupId: string
) => {
  return invoiceGroups.find((invoiceGroupItem) => {
    return invoiceGroupItem?.id === invoiceGroupId;
  });
};

const getInvalidMessage = (
  billingProfile?: NonNullable<
    FetchTransactionAccountForSetInvoiceGroupPaneQuery['transactionAccount']['availableInvoiceGroups']
  >['nodes'][number]
): React.ReactNode[] | null => {
  const invalidMessage: React.ReactNode[] = [];
  if (!billingProfile) {
    return null;
  }
  if (!billingProfile.defaultPaymentMethod) {
    invalidMessage.push(<div>No payment method is available for the billing profile</div>);
  }
  if (!billingProfile.shipToParty) {
    invalidMessage.push(<div>No sold-to address available for the billing profile</div>);
  }

  return invalidMessage;
};

const infoMessageForLinkedEntsTableLegacy = 'This action will also change the billing profile of these subscriptions:';
const infoMessageForLinkedEntsTable =
  'Any changes to the billing profile will also apply to other linked subscriptions.';

const saveECCFailureMessage = () => {
  return <div>You can still update it in the billing profile section, or contact {getSupportUrl()}.</div>;
};

export const consentTextLegacy = `By selecting 'Confirm' below, you acknowledge that the payment method on the new billing profile will be charged. The billing profile will also change for other product and app subscriptions on this site that belong to the same product family.`;

const consentTextUpdated = `By confirming, you acknowledge that the payment method on the new billing profile will be charged.`;

export const EntitlementSetInvoiceGroupPane = withApollo(
  ({ entitlement, refetch, isSandboxDecouplingEnabled = false }: Props) => {
    const startTime = new Date().getTime();
    const { closeSidebar } = useSidebar();
    const { eccDetailsLoading, getECCDetails } = useFetchEccFromContactService();
    const [selectedInvoiceGroupId, setSelectedInvoiceGroupId] = React.useState<string | undefined>(
      entitlement?.billingGroupId ?? undefined
    );
    const [atlassianAccountIds, setAtlassianAccountIds] = React.useState(['']);
    const [atlassianAccountEmails, setAtlassianAccountEmails] = React.useState<Array<string>>(['']);
    const [endCustomerContacts, setEndCustomerContacts] = useState<Array<EndCustomerContact>>([]);
    const [eccLoading, setEccLoading] = useState<boolean>(true);
    const [isContactsAvailable, setIsContactsAvailable] = useState<boolean>(false);
    const [allSecondaryCustomerContacts, setAllSecondaryCustomerContacts] = useState<EndCustomerContact[]>([]);
    const [hasExistingPrimaryContacts, setHasExistingPrimaryContacts] = useState<boolean>(false);
    const { getCreateContactRelationshipsPayload, getPatchPayloadForSetPrimaryContact } = useContactServicePayload();
    const isSandboxEntitlement = !!entitlement?.isSandbox;
    const [hasAddedNewPrimaryContact, setHasAddedNewPrimaryContact] = useState<boolean>(false);
    const [billingProfileSearch, setBillingProfileSearch] = useState<string>('');
    const [showBillingProfileForm, setShowBillingProfileForm] = useState<boolean>(false);

    React.useEffect(() => {
      (async () => {
        if (selectedInvoiceGroupId && entitlement.transactionAccountId) {
          setEccLoading(true);
          let data = await getECCDetails(selectedInvoiceGroupId, entitlement?.transactionAccountId);
          if (isPrimaryContactsEnabled) {
            const allSecondaryContacts = data?.filter((contact: ContactInfo) => !contact?.isPrimary);
            setAllSecondaryCustomerContacts(allSecondaryContacts);
          }
          if (isPrimaryContactsEnabled) {
            data = data?.filter((contact: ContactInfo) => contact?.isPrimary);
          }
          setEndCustomerContacts(data); // contains primary contacts if ecc-v3 is enabled else all the customer contacts linked to bp.
          const existingContacts = data as EndCustomerContact[];
          const accountIds = existingContacts?.map(({ accountId }) => accountId);
          const accountEmails = existingContacts?.map(({ email, name }) => email || name);
          const existingAccountIds = accountIds && accountIds.length > 0 ? accountIds : [''];
          const existingAccountEmails = accountEmails && accountEmails.length > 0 ? accountEmails : [''];
          setAtlassianAccountIds(existingAccountIds);
          setAtlassianAccountEmails(existingAccountEmails);
          setEccLoading(false);
        }
      })();
    }, [selectedInvoiceGroupId]);

    const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
    const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = React.useState(false);
    const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
      UpdateOrderRequest,
      any
    >({
      submitFormAction: updateEntitlementPlan,
      onComplete: () => {
        analytics.sendTrackEvent({
          action: AnalyticsTrackAction.CHANGED,
          actionSubject: AnalyticsTrackSubject.SWITCH_BILLING_PROFILE,
          attributes: {
            transactionAccountId: entitlement?.transactionAccountId,
            entitlementId: entitlement?.id,
          },
        });
        closeSidebar();
        refetch();
      },
    });

    const [isEnabled] = useFeatureToggle();
    const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
    const isNewBillingProfileChangeBehaviourEnabled = isEnabled(
      STATSIG_FEATURE_GATES.ENABLE_NEW_CHANGE_BILLING_PROFILE_BEHAVIOUR
    );
    const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
    const isNonCommercialPricingChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);
    const isPrimaryContactsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
    const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
    const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);

    const isCollectionProduct = isTWCPhase2Enabled && isCollectionProductEntitlement(entitlement?.productId);

    const [linkedEntitlementsData, setLinkedEntitlementsData] = React.useState<Array<LinkedDestinationEntitlement>>([]);
    let selectedBillingProfile:
      | NonNullable<
          FetchTransactionAccountForSetInvoiceGroupPaneQuery['transactionAccount']['availableInvoiceGroups']
        >['nodes'][number]
      | undefined = null;

    const { sendSuccessNotification, sendErrorNotification, notificationSent, setNotificationSent } = useNotification();

    const [selectionErrors, setSelectionErrors] = React.useState<React.ReactNode[] | null>(null);

    const { updateCustomerConsent } = useConsentHub();

    const consentText = isDesignIteration1Enabled ? consentTextUpdated : consentTextLegacy;

    React.useEffect(() => {
      setSelectionErrors(getInvalidMessage(selectedBillingProfile));
    }, [selectedBillingProfile]);

    React.useEffect(() => {
      if (atlassianAccountIds[0] !== '' || endCustomerContacts?.length > 0) {
        setIsContactsAvailable(true);
        setHasExistingPrimaryContacts(true);
      } else {
        setIsContactsAvailable(false);
        setHasExistingPrimaryContacts(false);
      }
    }, [atlassianAccountIds, endCustomerContacts]);

    React.useEffect(() => {
      if (allSecondaryCustomerContacts?.find((contact) => contact?.accountId === atlassianAccountIds[0])) {
        setHasAddedNewPrimaryContact(false);
      } else {
        setHasAddedNewPrimaryContact(true);
      }
    }, [atlassianAccountIds, allSecondaryCustomerContacts]);

    const isEntitlementRelationshipExists = React.useMemo(() => {
      if (isStatuspage(entitlement?.productId)) {
        return true;
      }
      return !!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length;
    }, [entitlement]);

    const constructRequestData = (entitlement: EntitlementDetails, formData: FormData) => {
      const requestData: UpdateOrderRequest = {
        entitlementId: entitlement.id,
        invoiceGroupId: formData?.invoiceGroupId?.value,
        offeringId: entitlement?.offeringId ?? '',
        productId: entitlement?.productId ?? '',
      };
      // send the entitlement, which is having active subscriptions
      if (isEntitlementRelationshipExists && isNewBillingProfileChangeBehaviourEnabled) {
        requestData.linkedEntitlements = activeLinkedEntitlements.length
          ? getLinkedEntitlementsDetails(entitlement, activeLinkedEntitlements)
          : [];
      } else if (isEntitlementRelationshipExists) {
        requestData.linkedEntitlements = linkedEntitlementsData.length
          ? getLinkedEntitlementsDetails(entitlement, linkedEntitlementsData)
          : [];
      }
      return requestData;
    };

    const contactServiceECCHandler = async (entitlement: EntitlementDetails, formData: FormData) => {
      try {
        const existingAccountIds = endCustomerContacts?.map((contact) => contact.accountId);
        const createContactIds = atlassianAccountIds?.filter((id) => !existingAccountIds?.includes(id));
        if (formData?.invoiceGroupId && entitlement?.transactionAccountId) {
          const createOrPatchContactPayload =
            !hasAddedNewPrimaryContact && isPrimaryContactsEnabled
              ? getPatchPayloadForSetPrimaryContact({
                  invoiceGroupId: formData?.invoiceGroupId?.value,
                  transactionAccountId: entitlement?.transactionAccountId,
                  primaryContactId: atlassianAccountIds[0],
                })
              : getCreateContactRelationshipsPayload({
                  invoiceGroupId: formData?.invoiceGroupId?.value,
                  transactionAccountId: entitlement?.transactionAccountId,
                  createContactIds: createContactIds,
                  ...(isPrimaryContactsEnabled && { primaryContactId: atlassianAccountIds[0] }),
                });

          //create contacts if required
          if (!isEmpty(createOrPatchContactPayload)) {
            if (isPrimaryContactsEnabled && !hasAddedNewPrimaryContact) {
              await patchContactRelationship(createOrPatchContactPayload as PatchContactRelationshipsRequest);
            } else {
              await createContactRelationship(createOrPatchContactPayload as CreateContactRelationshipsRequest);
            }
          }
        }
      } catch (err) {
        if (!notificationSent) {
          sendErrorNotification(
            errorNotificationBuilder()
              .withId('ecc-not-updated')
              .withHeader("We're unable to update customer contact")
              .withMessage(saveECCFailureMessage())
              .withDismissAfter(5000)
              .isVersion(NotificationTypes.FLAG)
              .build()
          );
          setNotificationSent(true);
        }
      }
    };

    const handleFormSubmit = async (formData: FormData) => {
      if (!isDesignIteration1Enabled && isEntitlementRelationshipExists) {
        setConfirmDialogOpen(true);
      } else {
        try {
          logTimeStamp(
            startTime,
            'Switch billing profile',
            SignalFxFEPerfStore.FE_USER_TASK_SWITCH_BILLING_PROFILE_TIME
          );
          const requestData = constructRequestData(entitlement, formData);
          await updateCustomerConsent(consentText);
          submitForm(requestData);
          contactServiceECCHandler(entitlement, formData);
        } catch {
          analytics.sendTrackEvent({
            action: AnalyticsTrackAction.FAILED,
            actionSubject: AnalyticsTrackSubject.SWITCH_BILLING_PROFILE,
            attributes: {
              transactionAccountId: entitlement?.transactionAccountId,
              entitlementId: entitlement?.id,
            },
          });
          const errorNotificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withId('switch-billing-profile-error')
            .withHeader("Sorry, we couldn't switch the billing profile.")
            .withMessage('Some error has occurred')
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(errorNotificationOptions);
          setNotificationSent(true);
        }
      }
    };

    const handleBillingProfileChange = (selectedItem: BillingProfileSelectItem) => {
      if (isDesignIteration1Enabled) {
        setAtlassianAccountEmails(['']);
        setAtlassianAccountIds(['']);
      }
      setSelectionErrors(getInvalidMessage(selectedItem?.invoiceGroup));
      if (selectedItem?.invoiceGroup?.id) {
        setSelectedInvoiceGroupId(selectedItem?.invoiceGroup?.id);
      }
    };

    if (complete && !notificationSent) {
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('update-ig-success')
        .withDismissAfter(DEFAULT_NOTIFICATION_VISIBLE_TIME);
      if (response.transitionTime === TransitionTime.SCHEDULED) {
        const notification = notificationOptions
          .withHeader('Billing profile switch scheduled')
          .withMessage('Billing profile switch has been scheduled for the next billing cycle')
          .build();
        sendSuccessNotification(notification);
        setNotificationSent(true);
      } else {
        const notification = notificationOptions
          .withHeader('Billing profile switched successfully')
          .withMessage(
            <div>
              <p>
                You can view the change in the <strong>order history</strong> tab.
              </p>
              <Link to={`#order-history`}>View order history</Link>
            </div>
          )
          .build();
        sendSuccessNotification(notification);
        setNotificationSent(true);
      }
    }

    const {
      data: transactionAccountData,
      loading: transactionAccountDataLoading,
      error: transactionAccountDataError,
    } = useQuery<
      FetchTransactionAccountForSetInvoiceGroupPaneQuery,
      FetchTransactionAccountForSetInvoiceGroupPaneQueryVariables
    >(fetchTransactionAccountForSetInvoiceGroupPane, {
      variables: {
        id: entitlement?.transactionAccountId ?? '',
      },
      displayName: 'fetchTransactionAccountForSetInvoiceGroupPane',
    });

    const transactionAccountId = entitlement?.transactionAccountId;
    const parentContainerId = getParentContainerId(entitlement, undefined, isJPDAsJiraFamilyEnabled);

    const { data: linkedEntitlements, loading: linkedEntitlementsLoading, error: linkedEntitlementsError } = useQuery<
      FetchLinkedEntitlementsPpcQuery,
      FetchLinkedEntitlementsPpcQueryVariables
    >(fetchLinkedEntitlementsPPC, {
      variables: {
        id: entitlement?.id,
        searchLinkedEntitlementParams: {
          entitlementId: entitlement?.id,
          containerId: parentContainerId,
          currentEntitlementType: currentEntitlementType(entitlement as EntitlementDetails),
          destinationEntitlementType: getDestinationLinkedEntitlementsType(
            isSandboxDecouplingEnabled,
            isSandboxEntitlement
          ),
          productType: entitlement?.productType,
          findApps: true,
          findProducts: true,
        },
        transactionAccountId,
      },
      displayName: 'fetchAllLinkedEntitlements',
      notifyOnNetworkStatusChange: true,
      skip: !isNewBillingProfileChangeBehaviourEnabled,
    });

    if (transactionAccountDataLoading || linkedEntitlementsLoading) return <LoadingIndicator />;

    const activeLinkedEntitlements =
      (linkedEntitlements?.entitlementDetails?.linkedEntitlements?.filter(
        (linkedEntitlementObj) => linkedEntitlementObj?.billingStatus === BillingStatus.ACTIVE
      ) as Array<LinkedDestinationEntitlement>) || [];

    const productType = entitlement?.productType;
    const isJiraProduct =
      productType === LinkedProductTypes.JIRA ||
      isProductBelongsToJiraFamily(entitlement?.productId, { isJPDAsJiraFamilyEnabled });

    let visibleLinkedEnts;
    if (isJiraProduct && parentContainerId) {
      visibleLinkedEnts = activeLinkedEntitlements?.filter(
        (linkedEnt) =>
          !(
            (linkedEnt?.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
            linkedEnt?.entitlementId === parentContainerId
          )
      );
    } else {
      visibleLinkedEnts = activeLinkedEntitlements?.filter(
        (linkedEnt) =>
          !(
            (linkedEnt?.isSandbox && linkedEnt?.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
            linkedEnt?.edition?.toUpperCase() === 'FAMILY'
          )
      );
    }

    if (transactionAccountDataError && !transactionAccountData?.transactionAccount) {
      const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
      return <GenericError error={transactionAccountDataError} />;
    }

    const billingProfiles = transactionAccountData?.transactionAccount?.availableInvoiceGroups?.nodes ?? [];

    selectedBillingProfile =
      entitlement?.billingGroupId && billingProfiles && billingProfiles.length
        ? getDefaultBillingGroup(billingProfiles, entitlement?.billingGroupId)
        : null;

    if (isDesignIteration1Enabled) {
      selectedBillingProfile =
        selectedInvoiceGroupId && billingProfiles?.length
          ? getDefaultBillingGroup(billingProfiles, selectedInvoiceGroupId)
          : null;
    }

    let filteredBillingProfiles: any[] = [];

    //if billing profile is empty then listing all billings in the dropdown
    filteredBillingProfiles = selectedBillingProfile
      ? billingProfiles.filter((item) => {
          const customerName = item?.shipToParty?.name;
          return customerName === selectedBillingProfile?.shipToParty?.name;
        })
      : billingProfiles;

    // Search Billing Profile
    if (isDesignIteration1Enabled && billingProfileSearch) {
      filteredBillingProfiles = filteredBillingProfiles.filter((item) => {
        const customerName = item?.name;
        return customerName?.toLowerCase().includes(billingProfileSearch.toLowerCase());
      });
    }

    filteredBillingProfiles = sortBillingProfiles(filteredBillingProfiles, true, false);

    // To Be Removed
    filteredBillingProfiles = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PREVENT_NON_COMMERCIAL_BILLING_PROFILES)
      ? filteredBillingProfiles.filter((billingProfile) => {
          return billingProfile?.shipToPriceEligibility
            ? ELIGIBLE_BILLING_PROFILES_FOR_AMENDMENT.includes(billingProfile?.shipToPriceEligibility?.toLowerCase())
            : true;
        })
      : filteredBillingProfiles;

    const customerBillingProfilePath = getRouteUrl(routePaths.customerAddresses, {
      txaId: entitlement?.transactionAccountId ?? '',
    });
    const handleConfirmDialog = async (formData: FormData) => {
      try {
        setConfirmDialogOpen(false);
        const requestData = constructRequestData(entitlement, formData);
        await updateCustomerConsent(consentText);
        submitForm(requestData);
        contactServiceECCHandler(entitlement, formData);
      } catch {
        const errorNotificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('switch-billing-profile-error')
          .withHeader("Sorry, we couldn't switch the billing profile.")
          .withMessage('Some error has occurred')
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(errorNotificationOptions);
        setNotificationSent(true);
      }
    };

    const isFreePlanEntitlement =
      entitlement?.edition === capitalize(PricingType.FREE) ||
      entitlement?.edition === capitalize(PricingType.LIMITED_FREE) ||
      entitlement?.offering?.pricingType === PricingType.FREE ||
      entitlement?.offering?.pricingType === PricingType.LIMITED_FREE;

    const isLinkedEntitlementsOnMonthlyPlan = visibleLinkedEnts?.some(
      (LinkedEntitlement) => LinkedEntitlement?.billingFrequency === BillingFrequency.MONTHLY
    );
    const hasNoLinkedEntitlements = visibleLinkedEnts?.length === 0;

    const primaryContactRequiredMsg = (
      <p>
        This billing profile does not have a primary customer contact. Set a contact as primary to switch billing
        profile.{' '}
      </p>
    );

    const renderSearchBillingProfile = () => {
      if (!isDesignIteration1Enabled || !showBillingProfileForm) {
        return null;
      }

      return (
        <div className="search-billing-profile">
          <Field name="selectBillingProfile" label="Select new billing profile">
            {({ fieldProps }) => (
              <Textfield
                {...fieldProps}
                name="selectBillingProfile"
                placeholder="Search"
                testId="search-new-billing-profile"
                value={fieldProps.value}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  fieldProps.onChange(event);
                  if (isDesignIteration1Enabled) {
                    setBillingProfileSearch((event.target as HTMLInputElement)?.value);
                  }
                }}
                elemBeforeInput={
                  <div className="search-icon">
                    <SearchIcon label="search-icon" size="small" />
                  </div>
                }
              />
            )}
          </Field>
        </div>
      );
    };

    const getHeader = () => {
      if (isDesignIteration1Enabled) {
        return (
          <Box paddingInlineStart="space.400">
            <Flex gap="space.100" alignItems="center">
              {/* Above 500 it will show 500+ in badge */}
              <Badge max={500}>{visibleLinkedEnts?.length ?? '-'}</Badge>
              <Text size="small" weight="bold">
                {isCollectionProduct ? 'included products' : 'linked subscriptions'}
              </Text>
            </Flex>
          </Box>
        );
      }
    };

    const renderFooter = () => {
      if (!isDesignIteration1Enabled) {
        return null;
      }

      if (showBillingProfileForm) {
        return null;
      }

      return (
        <div className="form-footer">
          <FormFooter align="end">
            <ButtonGroup>
              <Button onClick={() => closeSidebar()}>Cancel</Button>
              <LoadingButton
                appearance="primary"
                testId="continue-to-switch-billing-profile"
                onClick={() => {
                  setShowBillingProfileForm(true);
                }}
              >
                Continue
              </LoadingButton>
            </ButtonGroup>
          </FormFooter>
        </div>
      );
    };

    const renderLinkedEntitlementsTable = () => {
      return (
        <div
          className={classNames({
            'entitlement-relation-table': !isPrimaryContactsEnabled,
          })}
        >
          <LinkedEntitlementsTable
            message={isDesignIteration1Enabled ? infoMessageForLinkedEntsTable : infoMessageForLinkedEntsTableLegacy}
            messageType="warning"
            entitlementId={entitlement.id ?? ''}
            parentContainerID={parentContainerId}
            productType={entitlement.productType as LinkedProductTypes}
            currentEntitlementType={currentEntitlementType(entitlement)}
            destinationEntitlementType={getDestinationLinkedEntitlementsType(
              isSandboxDecouplingEnabled,
              isSandboxEntitlement
            )}
            findApps={true}
            findProducts={true}
            updateLinkedEntitlements={setLinkedEntitlementsData}
            setLoading={setLoadingLinkedEntitlements}
            transactionAccountId={entitlement.transactionAccountId}
            linkedEntitlementsData={linkedEntitlements}
            linkedEntitlementsLoading={linkedEntitlementsLoading}
            linkedEntitlementsError={linkedEntitlementsError}
            removeAddOnEntitlements={!isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS)}
            isSandboxSupportForSubscriptionsEnabled={isEnabled(
              STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
            )}
            productId={entitlement?.productId ?? ''}
            isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
            header={getHeader()}
          />
          {renderFooter()}
        </div>
      );
    };

    const renderLinkedEntitlements = () => {
      if (!isDesignIteration1Enabled) {
        return renderLinkedEntitlementsTable();
      }
    };

    const renderLinkedEntitlementsNew = () => {
      if (!isDesignIteration1Enabled) {
        return null;
      }

      if (showBillingProfileForm) {
        return null;
      }

      if (!visibleLinkedEnts?.length) {
        setShowBillingProfileForm(true);
        return null;
      }

      return renderLinkedEntitlementsTable();
    };

    const getConfirmButtonText = () => {
      if (isDesignIteration1Enabled) {
        return 'Confirm';
      }
      return isEntitlementRelationshipExists ? 'Continue' : 'Confirm';
    };

    const renderBillingProfileForm = (children: React.ReactNode) => {
      if (!isDesignIteration1Enabled) {
        return children;
      }

      if (showBillingProfileForm) {
        return children;
      }

      return null;
    };

    const renderAddPrimaryContact = () => {
      const isPrimaryContactExist = endCustomerContacts?.find((contact) => contact?.isPrimary);
      if (isDesignIteration1Enabled && isPrimaryContactExist) {
        return null;
      }

      if (eccLoading) {
        if (isDesignIteration1Enabled) {
          return null;
        }
        return (
          <SkeletonLoaderWrapper testId="order-review-ecc-loading">
            <SkeletonLoader height="40px" />
          </SkeletonLoaderWrapper>
        );
      }

      return (
        <AddContactSelectFields
          isExistingContactRemoveDisabled={true}
          isAddNewContactDisabled={true}
          setAtlassianAccountIds={setAtlassianAccountIds}
          atlassianAccountIds={atlassianAccountIds}
          atlassianAccountEmails={atlassianAccountEmails}
          setAtlassianAccountEmails={setAtlassianAccountEmails}
          currentUserList={atlassianAccountIds}
          userAlreadyExistText="Customer contact already exists"
          emailFieldText="Select or add new contact"
          shouldAllowAddingContact={true}
          textFieldLabel="Enter email"
          nameFieldText="Enter contact full name"
          availableOptions={allSecondaryCustomerContacts?.map((contact: EndCustomerContact) => ({
            fullName: contact?.name ?? '',
            email: contact?.email ?? '',
            picture: contact?.picture ?? '',
            atlassianAccountId: contact?.accountId ?? '',
          }))}
          isRemoveButtonHidden
        />
      );
    };

    const renderPrimaryContactInfoPanel = () => {
      if (isPrimaryContactsEnabled && !eccDetailsLoading && !hasExistingPrimaryContacts) {
        if (isDesignIteration1Enabled) {
          return (
            <Box paddingBlockStart="space.200">
              <Flex gap="space.100" alignItems="center">
                <WarningIcon primaryColor={token('color.icon.warning')} label="Warning" />
                <Text color="color.text.subtlest">{primaryContactRequiredMsg}</Text>
              </Flex>
            </Box>
          );
        }
        return (
          <div className="primary-contact-info-panel">
            <InfoPanel type="warning" message={primaryContactRequiredMsg} />
          </div>
        );
      }
    };

    const renderAddBillingProfileInfo = () => {
      return (
        <SectionMessageAction
          key="billing-info"
          href={`${customerBillingProfilePath}/${selectedBillingProfile?.shipToParty?.id}#billing-profiles`}
        >
          Add information to billing profile
        </SectionMessageAction>
      );
    };

    const renderErrorMessage = () => {
      if (!isEmpty(selectionErrors)) {
        if (isDesignIteration1Enabled) {
          const renderSelectionErrors = () => {
            if (selectionErrors && selectionErrors?.length > 0) {
              return (
                <div className="selection-errors">
                  <ul>
                    {selectionErrors?.map((error, index) => (
                      <li key={`error-${index + 1}`}>{error}</li>
                    ))}
                  </ul>
                </div>
              );
            }
            return selectionErrors;
          };

          return (
            <div className="switch-billing-profile-error">
              <SectionMessage
                title="Unable to switch billing profile"
                appearance="error"
                actions={[renderAddBillingProfileInfo()]}
              >
                <>
                  <Box paddingBlockEnd="space.200">{renderSelectionErrors()}</Box>
                  <p>
                    Add the information or switch the billing profile, then try again. Still having trouble? Please
                    reach out to {getSupportUrl()}
                  </p>
                </>
              </SectionMessage>
            </div>
          );
        }

        return (
          <SectionMessage
            title={isDesignIteration1Enabled ? 'Unable to switch billing profile ' : 'Update billing profile'}
            appearance={isDesignIteration1Enabled ? 'error' : 'warning'}
            actions={[renderAddBillingProfileInfo()]}
          >
            <>
              <p>The switch can’t be made because there’s:</p>
              <p>{selectionErrors}</p>
              <p>
                Add the information or switch the billing profile, then try again. Still having trouble? Please reach
                out to {getSupportUrl()}
              </p>
            </>
          </SectionMessage>
        );
      }
    };

    const renderFormError = () => {
      return (
        <FormSection>
          {!!formError && (
            <InfoPanel
              type="error"
              message={
                <ErrorComponents.ErrorMessageOnInfoPanel
                  title="We're unable to apply the changes"
                  message={formError}
                  helpContent={<HelpContent />}
                />
              }
            />
          )}
        </FormSection>
      );
    };

    return (
      <div
        className={classNames('entitlement-set-invoice-group-pane', {
          'entitlement-set-invoice-group-pane-v3': isDesignIteration1Enabled,
        })}
      >
        <div className="entitlement-update-invoice-group-form sidebar-pane">
          <div className="switch-billing-profile-info">
            <div className="header">
              <FormHeader title="Switch billing profile" />
            </div>
            {isDesignIteration1Enabled && renderErrorMessage()}
            {isDesignIteration1Enabled && renderFormError()}
            {renderLinkedEntitlementsNew()}
            {renderSearchBillingProfile()}
            {renderBillingProfileForm(
              <Form onSubmit={handleFormSubmit}>
                {({ formProps, getState }) => (
                  <form
                    className="form-section-switch-billing-profile"
                    data-testid="switch-billing-profile-form"
                    {...formProps}
                  >
                    <div className="content-section">
                      <FormSection>
                        <BillingProfileFormField
                          fieldLabel={'Select billing profile'}
                          productId={entitlement?.productId ?? undefined}
                          showTermPaymentMethodGroup={true}
                          billingProfiles={filteredBillingProfiles as InvoiceGroup[]}
                          entitlementBillingInvoiceGroupId={entitlement?.billingGroupId}
                          selected={selectedBillingProfile}
                          onChange={handleBillingProfileChange}
                          className="billing-profile-form-field"
                          billingFrequency={entitlement?.billingFrequency}
                          isSearchable={isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_SEARCH)}
                          isFreePlanEntitlement={isFreePlanEntitlement}
                          shouldHideTermPayment={isNewBillingProfileChangeBehaviourEnabled}
                          isLinkedEntitlementsOnMonthlyPlan={isLinkedEntitlementsOnMonthlyPlan}
                          hasNoLinkedEntitlements={hasNoLinkedEntitlements}
                          allowDisableBasedOnCurrency={isMultiCurrencyChangesEnabled}
                          allowDisableBasedOnPricingEligibility={isNonCommercialPricingChangesEnabled}
                          isDesignIteration1Enabled={isDesignIteration1Enabled}
                          primaryCustomerContact={{
                            isLoading: eccLoading,
                            data: endCustomerContacts.find((contact) => contact.isPrimary),
                          }}
                        />
                      </FormSection>
                      {isEntitlementRelationshipExists && !isPrimaryContactsEnabled && renderLinkedEntitlements()}
                      {renderPrimaryContactInfoPanel()}
                      {!isDesignIteration1Enabled && isPrimaryContactsEnabled && (
                        <div className="primary-contacts-heading">
                          <ContactsTooltip type={ContactsTooltipType.PRIMARY_CUSTOMER_CONTACT}>
                            <h3>Primary customer contact</h3>
                          </ContactsTooltip>
                        </div>
                      )}
                      {isPrimaryContactsEnabled ? (
                        renderAddPrimaryContact()
                      ) : (
                        <div
                          className={classNames({
                            'entitlement-relation-table': !isPrimaryContactsEnabled,
                          })}
                        >
                          <CustomPanel
                            testId="linked-customer-contacts"
                            expandedHeader={`View customer contacts`}
                            collapsedHeader={`View customer contacts`}
                          >
                            <FormSection>
                              {!isPrimaryContactsEnabled && (
                                <ContactsTooltip type={ContactsTooltipType.CUSTOMER}>
                                  <h3>Customer contacts</h3>
                                </ContactsTooltip>
                              )}
                              <Field name="customer-contact">
                                {() =>
                                  eccLoading ? (
                                    <SkeletonLoaderWrapper testId="order-review-ecc-loading">
                                      <SkeletonLoader height="40px" />
                                    </SkeletonLoaderWrapper>
                                  ) : (
                                    <AddContactFields
                                      setAtlassianAccountIds={setAtlassianAccountIds}
                                      atlassianAccountIds={atlassianAccountIds}
                                      atlassianAccountEmails={atlassianAccountEmails}
                                      setAtlassianAccountEmails={setAtlassianAccountEmails}
                                      currentUserList={atlassianAccountIds}
                                      userAlreadyExistText="Customer contact already exists"
                                      emailFieldText="Enter contact email"
                                      addButtonText="Add customer contact"
                                      isExistingContactRemoveDisabled={true}
                                      isAddNewContactDisabled={
                                        atlassianAccountIds.length >= getMaxLimitOfCustomerContact()
                                      }
                                      nameFieldText="Enter contact full name"
                                    />
                                  )
                                }
                              </Field>
                            </FormSection>
                          </CustomPanel>
                        </div>
                      )}
                      {isEntitlementRelationshipExists && isPrimaryContactsEnabled && renderLinkedEntitlements()}
                      {!isDesignIteration1Enabled && renderFormError()}
                      {!isDesignIteration1Enabled && <FormSection>{renderErrorMessage()}</FormSection>}
                      <FormSection>
                        {!isContactsAvailable && !isPrimaryContactsEnabled && (
                          <SectionMessage title="Update customer contacts" appearance="warning">
                            <>
                              <p>
                                Link at least one customer contact to this billing profile, then try again. Still having
                                trouble? Please reach out to{' '}
                                <a
                                  data-testid="partner-support-link"
                                  target="_blank"
                                  href={getPartnerServiceDeskUrlForGenericHelp()}
                                  rel="noreferrer"
                                  onClick={trackPartnerServiceDeskClick}
                                >
                                  Partner service desk
                                </a>
                              </p>
                            </>
                          </SectionMessage>
                        )}
                      </FormSection>
                      {isEntitlementRelationshipExists && (
                        <WarningDialog
                          isOpen={isConfirmDialogOpen}
                          onClose={() => setConfirmDialogOpen(false)}
                          onConfirm={() => handleConfirmDialog(getState().values)}
                          header={'Confirm switch'}
                          description={<p>{consentText}</p>}
                          okLabel={'Confirm'}
                          testId={'confirm-dialog-btn'}
                        />
                      )}
                    </div>
                    <div className={classNames('footer-section', { 'footer-section-v2': isDesignIteration1Enabled })}>
                      <div>
                        {!isDesignIteration1Enabled && !isEntitlementRelationshipExists && (
                          <FormSection>
                            <Divider />
                            <EditSubscriptionTermsAndConditions
                              isConfirmationCheckboxRequired={false}
                              tncTexts={[
                                (props) => (
                                  <p key="tnc-text-1" {...props}>
                                    {consentText}
                                  </p>
                                ),
                              ]}
                            />
                          </FormSection>
                        )}
                      </div>
                      <div className="form-footer">
                        <FormFooter align="end">
                          {isDesignIteration1Enabled && <Text color="color.text.subtle">{consentTextUpdated}</Text>}
                          <ButtonGroup>
                            <Button
                              appearance={isDesignIteration1Enabled ? undefined : 'subtle'}
                              onClick={() => closeSidebar()}
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              appearance="primary"
                              type="submit"
                              isLoading={isProcessing}
                              isDisabled={
                                selectedInvoiceGroupId === entitlement?.billingGroupId ||
                                !isEmpty(selectionErrors) ||
                                loadingLinkedEntitlement ||
                                (isNewBillingProfileChangeBehaviourEnabled && linkedEntitlementsLoading) ||
                                eccLoading ||
                                !isContactsAvailable
                              }
                              testId="submit-billing-profile"
                            >
                              {getConfirmButtonText()}
                            </LoadingButton>
                          </ButtonGroup>
                        </FormFooter>
                      </div>
                    </div>
                  </form>
                )}
              </Form>
            )}
          </div>
        </div>
      </div>
    );
  }
);
